import { useCallback, useEffect, useMemo, useState } from "react";
import { Container, Row, Col, Form, Button, Modal } from "react-bootstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import swal from "sweetalert";
import { useLocation, useNavigate } from "react-router-dom";
import { aiService } from "../../service/api.service";
import { InlineLoader } from "../loader";
import Breadcrumbs from "../common/Breadcrumbs.jsx";
import {
  SUBSCRIPTION_PLANS,
  SUBSCRIPTION_TYPES,
  DEFAULT_MODULES,
  PAYMENT_TYPES,
  getTodayDateValue,
  addDaysToDateValue,
  generateContractRef,
} from "./subscriptionPlans.js";

const ACCESS_OPTIONS = ["All", "Chat", "Calls"];

function needsOpenAi(accessOption) {
  return accessOption === "All" || accessOption === "Chat";
}

function needsTwilio(accessOption) {
  return accessOption === "All" || accessOption === "Calls";
}

function getModulesForAccess(accessOption) {
  if (accessOption === "Chat") {
    return DEFAULT_MODULES.filter((module) => module !== "Calls");
  }
  if (accessOption === "Calls") {
    return ["Dashboard", "Agents", "Calls", "Configuration"];
  }
  return Array.from(new Set([...DEFAULT_MODULES, "Calls"]));
}

export default function AddSubscription() {
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const userId = location.state?.userId || searchParams.get("user_id");
  const userName = location.state?.userName || searchParams.get("user_name") || "";

  const [subscriptions, setSubscriptions] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const initialContractRef = useMemo(() => generateContractRef(), []);
  const [agentLimitConflict, setAgentLimitConflict] = useState(null);
  const [selectedAgentIds, setSelectedAgentIds] = useState([]);
  const [applyingAgentLimit, setApplyingAgentLimit] = useState(false);

  const activeSubscription = subscriptions.find((sub) => sub.status === "active");
  const chainedSubscriptions = subscriptions.filter(
    (sub) => sub.status === "active" || sub.status === "scheduled",
  );
  const lastChainedEndDate =
    chainedSubscriptions[chainedSubscriptions.length - 1]?.subscriptionEndDate;
  const nextStartDate = lastChainedEndDate
    ? addDaysToDateValue(lastChainedEndDate, 1)
    : getTodayDateValue();

  const handleUnauthorized = () => {
    swal({ icon: "error", text: "Unauthorized", button: "OK" }).then(() => {
      localStorage.clear();
      window.location.href = "/";
    });
  };

  const loadSubscriptions = useCallback(async (id) => {
    setHistoryLoading(true);
    try {
      const res = await aiService.getUserSubscriptions(id);
      const records = res?.data?.data || [];
      setSubscriptions(records);
      return records;
    } catch (error) {
      if (error?.response?.status === 401) {
        handleUnauthorized();
      } else {
        swal({ icon: "error", text: "Failed to load subscription history." });
      }
      return [];
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  function computeNextStartDate(records) {
    const chained = records.filter(
      (sub) => sub.status === "active" || sub.status === "scheduled",
    );
    const lastEndDate = chained[chained.length - 1]?.subscriptionEndDate;
    return lastEndDate ? addDaysToDateValue(lastEndDate, 1) : getTodayDateValue();
  }

  useEffect(() => {
    if (!userId) {
      swal({
        icon: "error",
        text: "Please select a company to add a subscription.",
      }).then(() => {
        navigate("/company-list");
      });
      return;
    }
    loadSubscriptions(userId);
  }, [userId, navigate, loadSubscriptions]);

  const validationSchema = Yup.object().shape({
    subscriptionType: Yup.string().required("Subscription type is required"),
    subscriptionPrice: Yup.string()
      .trim()
      .required("Subscription price is required"),
    paymentType: Yup.string().required("Payment type is required"),
    accessOption: Yup.string()
      .oneOf(ACCESS_OPTIONS)
      .required("Access option is required"),
    allowedAgents: Yup.string().trim().required("Allowed agents is required"),
    subscriptionStartDate: Yup.string().required(
      "Subscription start date is required",
    ),
    subscriptionEndDate: Yup.string().required(
      "Subscription end date is required",
    ),
    openAiApi: Yup.string().trim(),
    twilioSid: Yup.string().trim(),
    twilioSecretKey: Yup.string().trim(),
  });

  async function submitSubscription(payload, values, { resetForm }) {
    try {
      const res = await aiService.addUserSubscription(userId, payload);
      await aiService.updateUserDetails(userId, {
        modules: getModulesForAccess(values.accessOption),
        openAiApi: needsOpenAi(values.accessOption) ? values.openAiApi : "",
        twilioSid: needsTwilio(values.accessOption) ? values.twilioSid : "",
        twilioSecretKey: needsTwilio(values.accessOption)
          ? values.twilioSecretKey
          : "",
      });
      const isQueued = res?.data?.data?.status === "scheduled";
      await swal({
        icon: "success",
        text:
          res?.data?.message ||
          (isQueued
            ? `Subscription queued to start on ${values.subscriptionStartDate}.`
            : "Subscription activated successfully."),
      });
      const updatedRecords = await loadSubscriptions(userId);
      resetForm({
        values: {
          subscriptionType: "Trial",
          subscriptionPrice: SUBSCRIPTION_PLANS.Trial.price,
          allowedAgents: String(SUBSCRIPTION_PLANS.Trial.allowedAgents),
          paymentType: PAYMENT_TYPES[0],
          accessOption: "All",
          subscriptionStartDate: computeNextStartDate(updatedRecords),
          subscriptionEndDate: "",
          contractRef: generateContractRef(),
          internalRemarks: "",
          openAiApi: "",
          twilioSid: "",
          twilioSecretKey: "",
        },
      });
    } catch (error) {
      if (error?.response?.status === 401) {
        handleUnauthorized();
        return;
      }
      if (error?.response?.status === 409 && error?.response?.data?.needsConfirmation) {
        setSelectedAgentIds([]);
        setAgentLimitConflict({ ...error.response.data, payload, values, resetForm });
        return;
      }
      swal({
        icon: "error",
        text: error?.response?.data?.message || "Unable to add subscription.",
      });
    }
  }

  function toggleAgentSelection(agentId) {
    setSelectedAgentIds((prev) =>
      prev.includes(agentId)
        ? prev.filter((id) => id !== agentId)
        : [...prev, agentId],
    );
  }

  function closeAgentLimitModal() {
    setAgentLimitConflict(null);
    setSelectedAgentIds([]);
  }

  async function confirmDisableAgents() {
    const overflow = agentLimitConflict.overflow;
    if (selectedAgentIds.length !== overflow) {
      swal({
        icon: "warning",
        text: `Please select exactly ${overflow} agent(s) to disable.`,
      });
      return;
    }
    setApplyingAgentLimit(true);
    try {
      const { payload, values, resetForm } = agentLimitConflict;
      await submitSubscription(
        { ...payload, confirmed: true, agentIdsToDisable: selectedAgentIds },
        values,
        { resetForm },
      );
      closeAgentLimitModal();
    } finally {
      setApplyingAgentLimit(false);
    }
  }

  if (!userId) return null;

  return (
    <div className="dashboard-main-area company-list-area create-user-page add-subscription-page">
      <Container fluid>
        <div className="dashboard-area-heading management-heading">
          <div className="create-user-shell add-subscription-shell">
            <div className="create-user-heading">
              <Breadcrumbs />
              <h1 className="company-page-title">Add Subscription</h1>
              <p className="company-page-subtitle">
                {userName
                  ? `Add or queue a subscription for ${userName}.`
                  : "Add or queue a subscription for this company."}
              </p>
            </div>

            <section className="create-user-card">
              <div className="create-user-card-header">
                <h2>Current subscription</h2>
              </div>
              <div className="create-user-card-body">
                {historyLoading ? (
                  <p className="create-user-card-note">Loading subscription status...</p>
                ) : activeSubscription ? (
                  <>
                    <div className="create-user-plan-summary">
                      <div>
                        <span>Type</span>
                        <strong>{activeSubscription.type || "-"}</strong>
                      </div>
                      <div>
                        <span>Active since</span>
                        <strong>
                          {activeSubscription.subscriptionStartDate || "-"}
                        </strong>
                      </div>
                      <div>
                        <span>Ends on</span>
                        <strong>
                          {activeSubscription.subscriptionEndDate || "-"}
                        </strong>
                      </div>
                    </div>
                  </>
                ) : (
                  <p className="create-user-card-note">
                    This company has no active subscription.
                  </p>
                )}
              </div>
            </section>

            <Formik
              enableReinitialize
              initialValues={{
                subscriptionType: "Trial",
                subscriptionPrice: SUBSCRIPTION_PLANS.Trial.price,
                allowedAgents: String(SUBSCRIPTION_PLANS.Trial.allowedAgents),
                paymentType: PAYMENT_TYPES[0],
                accessOption: "All",
                subscriptionStartDate: nextStartDate,
                subscriptionEndDate: "",
                contractRef: initialContractRef,
                internalRemarks: "",
                openAiApi: "",
                twilioSid: "",
                twilioSecretKey: "",
              }}
              validationSchema={validationSchema}
              onSubmit={async (values, { setSubmitting, resetForm }) => {
                setSubmitting(true);
                try {
                  const payload = {
                    subscriptionType: values.subscriptionType,
                    subscriptionStartDate: values.subscriptionStartDate,
                    subscriptionEndDate: values.subscriptionEndDate,
                    subscriptionPrice: values.subscriptionPrice,
                    allowedAgents: values.allowedAgents,
                    paymentType: values.paymentType,
                    accessOption: values.accessOption,
                    contractRef: values.contractRef,
                    internalRemarks: values.internalRemarks,
                    modules: getModulesForAccess(values.accessOption),
                    trialEndDate:
                      values.subscriptionType === "Trial"
                        ? values.subscriptionEndDate
                        : "",
                    renewalDate:
                      values.subscriptionType !== "Trial"
                        ? values.subscriptionEndDate
                        : "",
                  };
                  await submitSubscription(payload, values, { resetForm });
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
                setFieldValue,
              }) => {
                const isFutureStart =
                  values.subscriptionStartDate > getTodayDateValue();
                const showOpenAi = needsOpenAi(values.accessOption);
                const showTwilio = needsTwilio(values.accessOption);

                return (
                  <Form onSubmit={handleSubmit}>
                    <section className="create-user-card">
                      <div className="create-user-card-header">
                        <h2>Plan and payments</h2>
                      </div>
                      <div className="create-user-card-body">
                        <p className="create-user-card-note">
                          {isFutureStart
                            ? `This subscription will be queued and will automatically activate on ${values.subscriptionStartDate}, the day after the current subscription ends.`
                            : "This subscription will be activated immediately."}
                        </p>
                        <Row className="g-3">
                          <Col md={4}>
                            <Form.Group className="mb-3">
                              <Form.Label>
                                Subscription type <span>*</span>
                              </Form.Label>
                              <Form.Select
                                className="filter-select"
                                name="subscriptionType"
                                value={values.subscriptionType}
                                onChange={(event) => {
                                  const subscriptionType = event.target.value;
                                  const plan = SUBSCRIPTION_PLANS[subscriptionType];
                                  setFieldValue("subscriptionType", subscriptionType);
                                  setFieldValue("subscriptionPrice", plan.price);
                                  setFieldValue(
                                    "allowedAgents",
                                    String(plan.allowedAgents),
                                  );
                                }}
                              >
                                {SUBSCRIPTION_TYPES.map((type) => (
                                  <option key={type} value={type}>
                                    {type}
                                  </option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group className="mb-3">
                              <Form.Label>
                                Allowed agents <span>*</span>
                              </Form.Label>
                              <Form.Control
                                name="allowedAgents"
                                placeholder="e.g. 10 or Unlimited"
                                value={values.allowedAgents}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isInvalid={
                                  touched.allowedAgents && !!errors.allowedAgents
                                }
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.allowedAgents}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group className="mb-3">
                              <Form.Label>
                                Access option <span>*</span>
                              </Form.Label>
                              <Form.Select
                                className="filter-select"
                                name="accessOption"
                                value={values.accessOption}
                                onChange={(event) => {
                                  const accessOption = event.target.value;
                                  setFieldValue("accessOption", accessOption);
                                  if (!needsOpenAi(accessOption)) {
                                    setFieldValue("openAiApi", "");
                                  }
                                  if (!needsTwilio(accessOption)) {
                                    setFieldValue("twilioSid", "");
                                    setFieldValue("twilioSecretKey", "");
                                  }
                                }}
                              >
                                {ACCESS_OPTIONS.map((option) => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group className="mb-3">
                              <Form.Label>
                                Price <span>*</span>
                              </Form.Label>
                              <Form.Control
                                name="subscriptionPrice"
                                placeholder="e.g. $149 / month"
                                value={values.subscriptionPrice}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isInvalid={
                                  touched.subscriptionPrice &&
                                  !!errors.subscriptionPrice
                                }
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.subscriptionPrice}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group className="mb-3">
                              <Form.Label>
                                Payment type <span>*</span>
                              </Form.Label>
                              <Form.Select
                                className="filter-select"
                                name="paymentType"
                                value={values.paymentType}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isInvalid={
                                  touched.paymentType && !!errors.paymentType
                                }
                              >
                                {PAYMENT_TYPES.map((type) => (
                                  <option key={type} value={type}>
                                    {type}
                                  </option>
                                ))}
                              </Form.Select>
                              <Form.Control.Feedback type="invalid">
                                {errors.paymentType}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group className="mb-3">
                              <Form.Label>Payment reference number</Form.Label>
                              <Form.Control
                                name="contractRef"
                                value={values.contractRef}
                                disabled
                                readOnly
                              />
                            </Form.Group>
                          </Col>
                          <Col md={12}>
                            <Form.Group>
                              <Form.Label>Internal remarks</Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={3}
                                name="internalRemarks"
                                placeholder="Notes visible to super admins only..."
                                value={values.internalRemarks}
                                onChange={handleChange}
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                      </div>
                    </section>

                    <section className="create-user-card">
                      <div className="create-user-card-header">
                        <h2>Subscription dates</h2>
                      </div>
                      <div className="create-user-card-body">
                        <Row className="g-3">
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>
                                Subscription starts <span>*</span>
                              </Form.Label>
                              <Form.Control
                                type="date"
                                name="subscriptionStartDate"
                                value={values.subscriptionStartDate}
                                disabled
                                readOnly
                              />
                              <Form.Text>
                                Automatically set to the day after the current
                                subscription ends.
                              </Form.Text>
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>
                                Subscription ends <span>*</span>
                              </Form.Label>
                              <Form.Control
                                type="date"
                                name="subscriptionEndDate"
                                value={values.subscriptionEndDate}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isInvalid={
                                  touched.subscriptionEndDate &&
                                  !!errors.subscriptionEndDate
                                }
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.subscriptionEndDate}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                        </Row>
                      </div>
                    </section>

                    <section className="create-user-card">
                      <div className="create-user-card-header">
                        <h2>API configuration</h2>
                      </div>
                      <div className="create-user-card-body">
                        <Row className="g-3">
                          {showOpenAi ? (
                            <Col md={12}>
                              <Form.Group className="mb-3">
                                <Form.Label>OpenAI API key</Form.Label>
                                <Form.Control
                                  type="password"
                                  name="openAiApi"
                                  placeholder="sk-..."
                                  value={values.openAiApi}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  isInvalid={
                                    touched.openAiApi && !!errors.openAiApi
                                  }
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errors.openAiApi}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                          ) : null}
                          {showTwilio ? (
                            <>
                              <Col md={6}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Twilio SID</Form.Label>
                                  <Form.Control
                                    name="twilioSid"
                                    placeholder="AC..."
                                    value={values.twilioSid}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    isInvalid={
                                      touched.twilioSid && !!errors.twilioSid
                                    }
                                  />
                                  <Form.Control.Feedback type="invalid">
                                    {errors.twilioSid}
                                  </Form.Control.Feedback>
                                </Form.Group>
                              </Col>
                              <Col md={6}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Twilio secret key</Form.Label>
                                  <Form.Control
                                    type="password"
                                    name="twilioSecretKey"
                                    placeholder="Enter Twilio auth token"
                                    value={values.twilioSecretKey}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    isInvalid={
                                      touched.twilioSecretKey &&
                                      !!errors.twilioSecretKey
                                    }
                                  />
                                  <Form.Control.Feedback type="invalid">
                                    {errors.twilioSecretKey}
                                  </Form.Control.Feedback>
                                </Form.Group>
                              </Col>
                            </>
                          ) : null}
                        </Row>
                      </div>
                    </section>

                    <div className="create-user-actions">
                      <Button
                        type="button"
                        variant="link"
                        className="create-user-cancel-btn"
                        disabled={isSubmitting}
                        onClick={() => navigate("/company-list")}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="primary"
                        className="agent-primary-btn"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <InlineLoader />
                        ) : isFutureStart ? (
                          "Queue subscription"
                        ) : (
                          "Add subscription"
                        )}
                      </Button>
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </div>
        </div>
      </Container>

      <Modal
        show={!!agentLimitConflict}
        onHide={closeAgentLimitModal}
        centered
        dialogClassName="agent-limit-modal"
      >
        <Modal.Header closeButton className="agent-limit-modal-header">
          <div className="agent-limit-modal-title-wrap">
            <span className="agent-limit-modal-icon">!</span>
            <Modal.Title className="agent-limit-modal-title">
              Company exceeds the new agent limit
            </Modal.Title>
          </div>
        </Modal.Header>
        <Modal.Body className="agent-limit-modal-body">
          <p className="agent-limit-modal-copy">{agentLimitConflict?.message}</p>
          <div className="agent-limit-modal-summary">
            Selected {selectedAgentIds.length} of {agentLimitConflict?.overflow || 0}{" "}
            agent(s) to disable.
          </div>
          <div className="agent-limit-modal-list">
            {(agentLimitConflict?.agents || []).map((agent) => (
              <Form.Check
                key={agent._id}
                type="checkbox"
                id={`agent-limit-${agent._id}`}
                label={agent.name || "Untitled agent"}
                checked={selectedAgentIds.includes(agent._id)}
                onChange={() => toggleAgentSelection(agent._id)}
                className="agent-limit-modal-check"
              />
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer className="agent-limit-modal-footer">
          <Button variant="outline-secondary" onClick={closeAgentLimitModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            className="agent-primary-btn"
            onClick={confirmDisableAgents}
            disabled={
              applyingAgentLimit ||
              selectedAgentIds.length !== (agentLimitConflict?.overflow || 0)
            }
          >
            {applyingAgentLimit ? <InlineLoader /> : "Disable selected & continue"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
